import React, { useState } from 'react';
import { Dropdown, Avatar, message } from 'antd';
import { CheckOutlined, SettingOutlined, BankOutlined, DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const TenantSelector: React.FC = () => {
  const { currentTenant, tenants, switchTenant, refreshTenants, isPlatformAdmin } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Refresh tenants quando il dropdown si apre
  const handleDropdownOpenChange = async (open: boolean) => {
    if (open) {
      try {
        await refreshTenants();
      } catch (error) {
        console.error('Error refreshing tenants:', error);
      }
    }
  };

  // Render del trigger
  const renderTrigger = (showArrow = true) => (
    <div className="tenant-selector">
      <div className="tenant-selector__avatar">
        <BankOutlined style={{ fontSize: 14 }} />
      </div>
      <div className="tenant-selector__info">
        <span className="tenant-selector__label">Organizzazione</span>
        <span className="tenant-selector__name">
          {currentTenant?.name || 'Seleziona'}
        </span>
      </div>
      {showArrow && <DownOutlined className="tenant-selector__arrow" />}
    </div>
  );

  // Se non ci sono tenant o solo uno
  if (!tenants || tenants.length <= 1) {
    // Per platform admin, mostra comunque dropdown con link a gestione piattaforma
    if (isPlatformAdmin && currentTenant) {
      const adminMenuItems: MenuProps['items'] = [
        {
          key: 'current',
          label: (
            <div className="tenant-menu-item tenant-menu-item--active">
              <Avatar size={32} style={{ backgroundColor: '#EB2ADD' }}>
                {currentTenant.name.charAt(0).toUpperCase()}
              </Avatar>
              <div className="tenant-menu-item__info">
                <span className="tenant-menu-item__name">{currentTenant.name}</span>
                <span className="tenant-menu-item__status">Organizzazione corrente</span>
              </div>
              <CheckOutlined style={{ color: '#EB2ADD' }} />
            </div>
          ),
          disabled: true,
        },
        { type: 'divider' },
        {
          key: 'platform-admin',
          label: (
            <div className="tenant-menu-item tenant-menu-item--action">
              <SettingOutlined style={{ fontSize: 16 }} />
              <span>Gestione Piattaforma</span>
            </div>
          ),
          onClick: () => navigate('/admin/tenants'),
        },
      ];

      return (
        <Dropdown
          menu={{ items: adminMenuItems, className: 'tenant-dropdown' }}
          trigger={['click']}
          placement="bottomRight"
          onOpenChange={handleDropdownOpenChange}
        >
          <span>{renderTrigger()}</span>
        </Dropdown>
      );
    }

    // Mostra solo il nome del tenant corrente senza dropdown (utenti normali)
    if (currentTenant) {
      return renderTrigger(false);
    }
    return null;
  }

  const handleTenantSwitch = async (tenantId: number) => {
    if (tenantId === currentTenant?.id) return;

    setLoading(true);
    try {
      await switchTenant(tenantId);
      message.success('Organizzazione cambiata');
    } catch (error: any) {
      message.error(error.message || 'Errore nel cambio organizzazione');
    } finally {
      setLoading(false);
    }
  };

  const menuItems: MenuProps['items'] = [
    {
      key: 'header',
      label: (
        <div className="tenant-menu-header">
          <BankOutlined />
          <span>Cambia organizzazione</span>
        </div>
      ),
      disabled: true,
    },
    { type: 'divider' },
    ...tenants.map((tenant) => ({
      key: tenant.id.toString(),
      label: (
        <div className={`tenant-menu-item ${tenant.id === currentTenant?.id ? 'tenant-menu-item--active' : ''}`}>
          <Avatar
            size={32}
            style={{
              backgroundColor: tenant.id === currentTenant?.id ? '#EB2ADD' : '#010D69',
            }}
          >
            {tenant.name.charAt(0).toUpperCase()}
          </Avatar>
          <div className="tenant-menu-item__info">
            <span className="tenant-menu-item__name">{tenant.name}</span>
            {tenant.id === currentTenant?.id && (
              <span className="tenant-menu-item__status">Organizzazione corrente</span>
            )}
          </div>
          {tenant.id === currentTenant?.id && (
            <CheckOutlined style={{ color: '#EB2ADD' }} />
          )}
        </div>
      ),
      onClick: () => handleTenantSwitch(tenant.id),
    })),
  ];

  // Aggiungi opzione Platform Admin se applicabile
  if (isPlatformAdmin) {
    menuItems.push(
      { type: 'divider' },
      {
        key: 'platform-admin',
        label: (
          <div className="tenant-menu-item tenant-menu-item--action">
            <SettingOutlined style={{ fontSize: 16 }} />
            <span>Gestione Piattaforma</span>
          </div>
        ),
        onClick: () => navigate('/admin/tenants'),
      }
    );
  }

  return (
    <Dropdown
      menu={{ items: menuItems, className: 'tenant-dropdown' }}
      trigger={['click']}
      disabled={loading}
      placement="bottomRight"
      onOpenChange={handleDropdownOpenChange}
    >
      <span>{renderTrigger()}</span>
    </Dropdown>
  );
};

export default TenantSelector;
